import { v4 as uuid } from 'uuid';
import { assign } from 'lodash';
import { ChangeDetectionStrategy, Component, type OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatDesigns from "@medsurf/flat-designs";
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { ModalControlComponent } from "@medsurf/flat-components";

@Component({
    selector: 'medsurf-user-detail',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './user-detail.component.html',
    styleUrls: ['./user-detail.component.scss'],
    standalone: false
})
export class UserDetailComponent implements OnInit {
  @ViewChild('emailInputText', {static: false}) emailInputText: FlatDesigns.TextInputFormControlDesign;
  
  isSubmitted = signal(false);
  #user: FlatModels.UserEntityModels.User | null;
  #metadata: FlatModels.MetadataEntityModels.Metadata | null;
  userForm = new FormGroup(
    {
      email: new FormControl(''),
      role: new FormControl<FlatModels.UserEntityModels.Role>(FlatModels.UserEntityModels.Role.enum.viewer),
      firstName: new FormControl(''),
      lastName: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.UserEntityModels.UserSchema
        .pick({
          email: true,
          role: true,
          firstName: true,
          lastName: true
        })
      ),
      updateOn: 'change',
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public appSharedFacade: FlatFacades.AppSharedFacade,
              public alertControlFacade: FlatFacades.AlertControlFacade,
              public historySharedFacade: FlatFacades.HistorySharedFacade,
              public navigationControlFacade: FlatFacades.NavigationControlFacade,
              public userSharedFacade: FlatFacades.UserSharedFacade,
              public dialogService: DialogService,
              public translate: TranslateService) {
    // Update User
    this.userSharedFacade.user$.pipe(takeUntilDestroyed()).subscribe((user) => {
      this.#user = structuredClone(user);
      if (this.#user) {
        this.userForm.reset(this.#user);
      }
      this.isSubmitted.set(false);
    });
  }

  ngOnInit() {
    // Autofocus
    setTimeout(() => {
      if (this.emailInputText && this.emailInputText.inputElement) {
        this.emailInputText.inputElement.nativeElement.focus();
      }
    });
  }

  onSubmit() {
    if (this.isSubmitted() || this.userForm.invalid) {
      return;
    }
    this.isSubmitted.set(true);

    if (this.#user) {
      // Update User
      const user = assign(this.#user, this.userForm.value);
      this.userSharedFacade.requestUpdateUser(user).subscribe({
        error: (result) => {
          this.isSubmitted.set(false);
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.userForm);
        },
        next: () => {
          this.isSubmitted.set(false);
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    } else {
      // Add User
      const user = assign({id: uuid()}, this.userForm.value);
      this.userSharedFacade.requestAddUser(user as FlatModels.UserEntityModels.User).subscribe({
        error: (result) => {
          this.isSubmitted.set(false);
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.userForm);
        },
        next: () => {
          this.isSubmitted.set(false);
          this.navigationControlFacade.requestNavigate(['user', user.id]);
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    }
  }

  deleteUser() {
    if (!this.#user) return;
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.user_remove'),
      data: {
        label: 'global.user_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (this.#user && remove) {
        this.userSharedFacade.requestRemoveUser(this.#user).subscribe({
          error: (result) => {
            ZodSharedValidator.handleError(result, this.alertControlFacade, this.userForm);
          },
          next: () => {
            this.navigationControlFacade.requestNavigate(['users']);
            this.alertControlFacade.requestAddAlert({
              key: 'main',
              detail: 'notification.saved',
              severity: FlatModels.AlertControlModels.AlertType.enum.success,
              life: FlatModels.AlertControlModels.AlertTime.DEFAULT
            });
          }
        });
      }
    });
  }

  canDelete() {
    return this.#user && this.#user.id && this.#user.role !== FlatModels.UserEntityModels.Role.enum.admin;
  }
}
